import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { sunDirectionFromAzimuthElevation } from '../lighting/sunMath';
import type { EnvironmentConfig, SkyConfig } from './types';

/**
 * Owns image-based lighting setup (PMREM), the visible sky dome, and
 * atmospheric fog — knows how to turn an EnvironmentConfig into correctly
 * configured scene state. Course supplies the config; this class supplies
 * the mechanism, same split as LightingSystem/LightingConfig.
 *
 * Sky mode renders two separate `Sky` dome instances: one added directly
 * to the scene as the visible background (crisp, full resolution), and a
 * second, hidden one with its sun disc suppressed that gets PMREM-baked
 * into `scene.environment` for reflections/ambient light. Baking the
 * visible sky's own sun disc into the environment map tends to blow out
 * reflections with a hard bright blob — suppressing it for the bake while
 * keeping it for the visible dome is the standard technique.
 */
export class EnvironmentSystem {
  private readonly pmrem: THREE.PMREMGenerator;
  private currentEnvTexture: THREE.Texture | null = null;
  private skyMesh: Sky | null = null;

  constructor(
    renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
  ) {
    this.pmrem = new THREE.PMREMGenerator(renderer);
  }

  async applyConfig(config: EnvironmentConfig): Promise<void> {
    await this.applySky(config.sky);
    this.applyFog(config.fog);
  }

  private async applySky(config: SkyConfig): Promise<void> {
    if (config.kind !== 'sky') this.teardownSkyMesh();

    if (config.kind === 'procedural') {
      const target = this.pmrem.fromScene(new RoomEnvironment(), 0.04);
      this.setEnvironmentTexture(target.texture, false);
      return;
    }

    if (config.kind === 'hdri') {
      this.pmrem.compileEquirectangularShader();
      const texture = await new RGBELoader().loadAsync(config.url);
      const target = this.pmrem.fromEquirectangular(texture);
      texture.dispose();
      this.setEnvironmentTexture(target.texture, config.useAsBackground ?? true);
      return;
    }

    this.applyProceduralSky(config);
  }

  private applyProceduralSky(config: Extract<SkyConfig, { kind: 'sky' }>): void {
    const direction = sunDirectionFromAzimuthElevation(
      config.sunAzimuthDeg,
      config.sunElevationDeg,
    );
    const turbidity = config.turbidity ?? 2;
    const rayleigh = config.rayleigh ?? 1;
    const mieCoefficient = config.mieCoefficient ?? 0.005;
    const mieDirectionalG = config.mieDirectionalG ?? 0.8;

    if (!this.skyMesh) {
      this.skyMesh = new Sky();
      this.skyMesh.scale.setScalar(10000);
      this.scene.add(this.skyMesh);
    }
    const visibleUniforms = this.skyMesh.material.uniforms;
    visibleUniforms.turbidity.value = turbidity;
    visibleUniforms.rayleigh.value = rayleigh;
    visibleUniforms.mieCoefficient.value = mieCoefficient;
    visibleUniforms.mieDirectionalG.value = mieDirectionalG;
    visibleUniforms.sunPosition.value.copy(direction);
    visibleUniforms.showSunDisc.value = 1;

    // Separate hidden instance, sun disc suppressed, used only for the PMREM bake.
    const bakeSky = new Sky();
    bakeSky.scale.setScalar(10000);
    const bakeUniforms = bakeSky.material.uniforms;
    bakeUniforms.turbidity.value = turbidity;
    bakeUniforms.rayleigh.value = rayleigh;
    bakeUniforms.mieCoefficient.value = mieCoefficient;
    bakeUniforms.mieDirectionalG.value = mieDirectionalG;
    bakeUniforms.sunPosition.value.copy(direction);
    bakeUniforms.showSunDisc.value = 0;

    const bakeScene = new THREE.Scene();
    bakeScene.add(bakeSky);
    const target = this.pmrem.fromScene(bakeScene, 0.02);
    this.setEnvironmentTexture(target.texture, false); // false: the real Sky mesh in `scene` is the visible background

    bakeSky.geometry.dispose();
    bakeSky.material.dispose();
  }

  private applyFog(fog: EnvironmentConfig['fog']): void {
    this.scene.fog = fog ? new THREE.Fog(fog.color, fog.near, fog.far) : null;
  }

  private teardownSkyMesh(): void {
    if (!this.skyMesh) return;
    this.scene.remove(this.skyMesh);
    this.skyMesh.geometry.dispose();
    this.skyMesh.material.dispose();
    this.skyMesh = null;
  }

  private setEnvironmentTexture(texture: THREE.Texture, useAsBackground: boolean): void {
    this.currentEnvTexture?.dispose();
    this.currentEnvTexture = texture;
    this.scene.environment = texture;
    this.scene.background = useAsBackground ? texture : null;
  }

  dispose(): void {
    this.teardownSkyMesh();
    this.currentEnvTexture?.dispose();
    this.pmrem.dispose();
  }
}
